import React, { useEffect, useRef } from 'react';

import { GLOBE_LAND_BITS, GLOBE_POINTS } from './globeLand';

// The globe's diameter as a share of the frame's shorter side (the reference leaves a wide margin
// round its sphere).
const GLOBE_FILL = 0.72;
// The view: North America in front, looked at from 23°N so the pole stays off the top edge.
const START_LNG = -96;
const VIEW_LAT = 23;
// The slow turn (Lucas: "maybe slow rotate", 2026-10-01): the globe drifts 30° either way of the
// view and back, once every 50 s, instead of spinning round. A full turn hid every WAL city for half
// of each minute and left the empty Pacific in front; the drift keeps them all on the visible side.
const DRIFT_DEG = 30;
const DRIFT_MS = 50_000;
// 30 frames a second: at this speed a point moves about a pixel a frame, so it stays smooth at half
// the drawing work of a 60 Hz loop.
const FRAME_MS = 1000 / 30;

// Canvas takes colour strings, not Tailwind classes, so the tokens' values are repeated: white,
// utilityBlack700 (the top bar), which is also the reference's dot colour, and primary.
const DOT_RGB = '26, 26, 26';
const MARKER = '#444CE7';
const MARKER_RGB = '68, 76, 231';
const WHITE = '#FFFFFF';

// Land dots fade toward the edge of the sphere, as the reference's do: drawn in a few depth bands,
// one canvas path each, so a frame costs a handful of fills.
const DEPTH_BANDS = 6;

// The pulse: a soft disc of the marker colour grows out of each city dot and fades, staggered so
// the cities do not beat together (Lucas: "like pulsating dots", 2026-10-01).
const PULSE_MS = 2800;
const PULSE_ALPHA = 0.45;
// How far the disc grows, in dot radii past the dot.
const PULSE_REACH = 3.5;

// The sphere's shading, measured on the reference (cobe.vercel.app, 2026-10-01): white to about 55%
// of the radius, then a soft gray band to the limb, lightening again on a thin rim.
const SPHERE_SHADING: [number, number][] = [
    [0.55, 0],
    [0.65, 0.02],
    [0.73, 0.033],
    [0.82, 0.056],
    [0.94, 0.064],
    [0.99, 0.03],
    [1, 0],
];

// WAL's main cities. Picked from the last year's order leg endpoints in prod (read-only,
// 2026-10-01), suburbs folded into their metro (Mississauga, Vaughan and Burlington into Toronto,
// Laval into Montreal, Surrey into Vancouver, Dartmouth into Halifax, Newark into New York), each at
// the city's own coordinates, [lng, lat].
const MARKER_CITIES: [number, number][] = [
    [-73.5674, 45.5019], // Montreal
    [-79.3832, 43.6532], // Toronto
    [-113.4938, 53.5461], // Edmonton
    [-114.0719, 51.0447], // Calgary
    [-97.1384, 49.8951], // Winnipeg
    [-123.1207, 49.2827], // Vancouver
    [-106.67, 52.1332], // Saskatoon
    [-63.5752, 44.6488], // Halifax
    [-84.388, 33.749], // Atlanta
    [-74.006, 40.7128], // New York
    [-95.3698, 29.7604], // Houston
    [-87.6298, 41.8781], // Chicago
    [-80.1918, 25.7617], // Miami
];

type Vector = [number, number, number];

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

// x east at the 0° meridian, y north, z toward the viewer at 0°.
const toVector = (lng: number, lat: number): Vector => {
    const phi = toRadians(lat);
    const lambda = toRadians(lng);

    return [Math.cos(phi) * Math.sin(lambda), Math.sin(phi), Math.cos(phi) * Math.cos(lambda)];
};

// The same Fibonacci lattice as scripts/generate-globe-land.mjs, kept to the points its bitset
// marks as land.
const getLandPoints = (): Vector[] => {
    const bits = atob(GLOBE_LAND_BITS);
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    const points: Vector[] = [];

    for (let i = 0; i < GLOBE_POINTS; i++) {
        if ((bits.charCodeAt(i >> 3) >> (i & 7)) & 1) {
            const y = 1 - ((i + 0.5) * 2) / GLOBE_POINTS;
            const ringRadius = Math.sqrt(1 - y * y);
            points.push([Math.cos(goldenAngle * i) * ringRadius, y, Math.sin(goldenAngle * i) * ringRadius]);
        }
    }

    return points;
};

/**
 * PublicTrackingGlobe — the public tracking page's backdrop before a search, recreated from the
 * Cobe globe (cobe.vercel.app, the reference Lucas had picked, 2026-10-01): land as fine dark dots
 * spread evenly over a white sphere shaded gray toward its edge, drifting slowly to and fro, with
 * WAL's main cities as pulsing dots. Drawn on one 2D canvas with no map library: the Mapbox version laid a
 * flat dot texture on its globe, which stretched and blurred on large screens ("very ugly, too
 * static"). No glow, no stars, no labels (earlier rulings the same day). Decorative only: no input,
 * hidden from assistive tech. With reduced motion asked for, it draws one still frame.
 */
const PublicTrackingGlobe = () => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');
        if (!wrapper || !canvas || !context) return undefined;

        const landPoints = getLandPoints();
        const markers = MARKER_CITIES.map(([lng, lat]) => toVector(lng, lat));
        const bands: number[][] = Array.from({ length: DEPTH_BANDS }, () => []);
        const isStill = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
        const tiltSin = Math.sin(toRadians(VIEW_LAT));
        const tiltCos = Math.cos(toRadians(VIEW_LAT));
        let width = 0;
        let height = 0;
        let frame = 0;
        const startedAt = performance.now();

        const draw = (now: number) => {
            if (!width || !height) return;
            const elapsed = isStill ? 0 : now - startedAt;
            const centreLng = toRadians(START_LNG + DRIFT_DEG * Math.sin((elapsed / DRIFT_MS) * Math.PI * 2));
            const spinSin = Math.sin(centreLng);
            const spinCos = Math.cos(centreLng);
            const cx = width / 2;
            const cy = height / 2;
            const radius = (Math.min(width, height) * GLOBE_FILL) / 2;
            const dotRadius = Math.max(0.9, radius * 0.007);
            const markerRadius = Math.min(4, Math.max(2.25, radius * 0.014));

            // Turn the point to the view's longitude, then tilt it to the view's latitude.
            const project = ([x, y, z]: Vector): Vector => {
                const turnedX = x * spinCos - z * spinSin;
                const turnedZ = x * spinSin + z * spinCos;

                return [cx + turnedX * radius, cy - (y * tiltCos - turnedZ * tiltSin) * radius, y * tiltSin + turnedZ * tiltCos];
            };

            context.clearRect(0, 0, width, height);

            const shading = context.createRadialGradient(cx, cy, 0, cx, cy, radius);
            SPHERE_SHADING.forEach(([stop, alpha]) => shading.addColorStop(stop, `rgba(${DOT_RGB}, ${alpha})`));
            context.fillStyle = shading;
            context.beginPath();
            context.arc(cx, cy, radius, 0, Math.PI * 2);
            context.fill();

            bands.forEach((band) => {
                band.length = 0;
            });
            landPoints.forEach((point) => {
                const [sx, sy, depth] = project(point);
                if (depth <= 0) return;
                bands[Math.min(DEPTH_BANDS - 1, Math.floor(depth * DEPTH_BANDS))].push(sx, sy);
            });
            bands.forEach((band, index) => {
                const depth = (index + 0.5) / DEPTH_BANDS;
                const r = dotRadius * (0.7 + 0.3 * depth);
                context.fillStyle = `rgba(${DOT_RGB}, ${Math.min(1, 0.15 + depth * 1.4)})`;
                context.beginPath();

                for (let i = 0; i < band.length; i += 2) {
                    context.moveTo(band[i] + r, band[i + 1]);
                    context.arc(band[i], band[i + 1], r, 0, Math.PI * 2);
                }

                context.fill();
            });

            markers.forEach((marker, index) => {
                const [sx, sy, depth] = project(marker);
                if (depth <= 0.05) return;
                // Cities fade in and out over the edge instead of popping.
                const fade = Math.min(1, (depth - 0.05) / 0.25);

                if (!isStill) {
                    const pulse = (((elapsed / PULSE_MS + index / markers.length) % 1) + 1) % 1;
                    context.fillStyle = `rgba(${MARKER_RGB}, ${PULSE_ALPHA * (1 - pulse) ** 1.5 * fade})`;
                    context.beginPath();
                    // Ease out: the disc leaves the dot quickly and slows as it fades.
                    context.arc(sx, sy, markerRadius * (1 + (1 - (1 - pulse) ** 2) * PULSE_REACH), 0, Math.PI * 2);
                    context.fill();
                }

                context.globalAlpha = fade;
                context.beginPath();
                context.arc(sx, sy, markerRadius, 0, Math.PI * 2);
                context.fillStyle = MARKER;
                context.fill();
                context.lineWidth = 1.5;
                context.strokeStyle = WHITE;
                context.stroke();
                context.globalAlpha = 1;
            });
        };

        let drawnAt = 0;

        const loop = (now: number) => {
            // The few ms of slack keep a 60 Hz screen on every other frame instead of skipping two.
            if (now - drawnAt >= FRAME_MS - 4) {
                drawnAt = now;
                draw(now);
            }

            frame = window.requestAnimationFrame(loop);
        };

        // Sized in device pixels, so the dots stay sharp on high-density screens.
        const resize = () => {
            const ratio = Math.min(window.devicePixelRatio || 1, 2);
            width = wrapper.clientWidth;
            height = wrapper.clientHeight;
            canvas.width = Math.round(width * ratio);
            canvas.height = Math.round(height * ratio);
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            context.setTransform(ratio, 0, 0, ratio, 0, 0);
            if (isStill) draw(performance.now());
        };

        const resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(wrapper);
        resize();
        if (!isStill) frame = window.requestAnimationFrame(loop);

        return () => {
            window.cancelAnimationFrame(frame);
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <div ref={wrapperRef} className="relative h-full w-full overflow-hidden bg-white">
            <canvas ref={canvasRef} aria-hidden className="absolute inset-0 pointer-events-none" />
        </div>
    );
};

export default PublicTrackingGlobe;
